import { computed, onUnmounted, reactive, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  fetchByDirection,
  fetchByLogist,
  fetchBotAuctions,
  fetchBotByDay,
  fetchBotSummary,
  fetchDirectionLifecycle,
  fetchFilterOptions,
  fetchKpis,
  fetchSegments,
  fetchStatusByLogist,
  fetchStatusSummary,
  fetchTemporal,
  fetchTrips,
  fetchTripsByDay,
} from '@/backend/statisticsApi';
import { presetRange } from './components/DatePresetsDropdown.vue';
import { toIsoDate } from './format';

export const TRIPS_PAGE_SIZE = 50;
const STORAGE_KEY = 'statisticsFilters';

// Шаг корзин вкладки «Динамика»
const STEP_STORAGE_KEY = 'temporalStep';
const VALID_STEPS = ['hour', 'day', 'week'];
const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_STEP_MAX_DAYS = 41; // кэп 1000 корзин / 24 часа

function isValidPreset(id) {
  return typeof id === 'string' && (id === 'custom' || presetRange(id) !== null);
}

function defaultPeriod() {
  return presetRange('d90');
}

function loadStoredFilters() {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

const LIST_KEYS = ['logists', 'citiesOut', 'citiesIn', 'segments', 'statuses', 'execution', 'closingTiming', 'clientCategories'];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Общее состояние фильтров общей и временной статистики: один localStorage-ключ
// (statisticsFilters) — фильтры, дата-основа и пресет периода переносится
// между страницами раздела.
export function useStatisticsFilters() {
  const stored = loadStoredFilters();
  const defaults = defaultPeriod();
  const filters = reactive({
    // Пресет хранится явно: «текущие» периоды (месяц/квартал/год, N дней)
    // пересчитываются от сегодняшней даты при каждой загрузке страницы.
    presetId: isValidPreset(stored?.presetId) ? stored.presetId : 'd90',
    dateFrom:
      typeof stored?.dateFrom === 'string' && DATE_RE.test(stored.dateFrom)
        ? stored.dateFrom
        : defaults.dateFrom,
    dateTo:
      typeof stored?.dateTo === 'string' && DATE_RE.test(stored.dateTo)
        ? stored.dateTo
        : defaults.dateTo,
    logists: [],
    citiesOut: [],
    citiesIn: [],
    // Объединённый город-фильтр (отправка ИЛИ назначение)
    linkedCities: stored?.linkedCities === true,
    citiesAny: [],
    segments: [],
    statuses: [],
    execution: [],
    closingTiming: [],
    // Категория клиента (top/key/regular/none) — Mongo client_category
    clientCategories: [],
    trip: '',
    // Бот: '' — все, 'bot' — только бот, 'human' — кроме бота
    bot:
      stored?.bot === 'bot' || stored?.bot === 'human'
        ? stored.bot
        : '',
    // Дата-основа периода/дневных корзин (stat_date | card_create_dt | closed_at)
    dateBasis:
      typeof stored?.dateBasis === 'string' && stored.dateBasis
        ? stored.dateBasis
        : 'card_create_dt',
  });

  const applyPreset = (id) => {
    const range = presetRange(id);
    if (!range) return;
    filters.dateFrom = range.dateFrom;
    filters.dateTo = range.dateTo;
  };

  // Восстановление: не-custom пресет ре-применяем к сегодняшнему дню,
  // чтобы «Текущий месяц» не застывал вчерашним интервалом.
  if (filters.presetId !== 'custom') {
    applyPreset(filters.presetId);
  }
  for (const key of LIST_KEYS) {
    if (Array.isArray(stored?.[key])) {
      filters[key] = stored[key].filter((v) => typeof v === 'string');
    }
  }
  if (Array.isArray(stored?.citiesAny)) {
    filters.citiesAny = stored.citiesAny.filter((v) => typeof v === 'string');
  }
  if (typeof stored?.trip === 'string') {
    filters.trip = stored.trip.trim();
  }

  // Применение снапшота пресета фильтров: та же валидация, что при restore.
  // «Текущие» пресеты (месяц/квартал/…) ре-применяются от сегодняшнего дня.
  const applySnapshot = (snap) => {
    if (!snap || typeof snap !== 'object') return;
    if (isValidPreset(snap.presetId) && snap.presetId !== 'custom') {
      filters.presetId = snap.presetId;
      applyPreset(snap.presetId);
    } else {
      filters.presetId = 'custom';
      if (typeof snap.dateFrom === 'string' && DATE_RE.test(snap.dateFrom)) {
        filters.dateFrom = snap.dateFrom;
      }
      if (typeof snap.dateTo === 'string' && DATE_RE.test(snap.dateTo)) {
        filters.dateTo = snap.dateTo;
      }
    }
    for (const key of LIST_KEYS) {
      if (Array.isArray(snap[key])) {
        filters[key] = snap[key].filter((v) => typeof v === 'string');
      }
    }
    if (Array.isArray(snap.citiesAny)) {
      filters.citiesAny = snap.citiesAny.filter((v) => typeof v === 'string');
    }
    filters.linkedCities = snap.linkedCities === true;
    if (typeof snap.trip === 'string') {
      filters.trip = snap.trip.trim();
    }
    if (snap.bot === 'bot' || snap.bot === 'human') {
      filters.bot = snap.bot;
    } else {
      filters.bot = '';
    }
    if (typeof snap.dateBasis === 'string' && snap.dateBasis) {
      filters.dateBasis = snap.dateBasis;
    }
  };

  watch(
    filters,
    (value) => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      } catch (e) {
        /* localStorage может быть недоступен — молча игнорируем */
      }
    },
    { deep: true }
  );

  const resetFilters = () => {
    // Сброс — на текущий месяц
    filters.presetId = 'this_month';
    applyPreset('this_month');
    filters.logists = [];
    filters.citiesOut = [];
    filters.citiesIn = [];
    filters.linkedCities = false;
    filters.citiesAny = [];
    filters.segments = [];
    filters.statuses = [];
    filters.execution = [];
    filters.closingTiming = [];
    filters.clientCategories = [];
    filters.trip = '';
    filters.bot = '';
    filters.dateBasis = 'card_create_dt';
  };

  // Смена суток при открытой странице: «текущие» пресеты сдвигаем вперёд.
  let lastDay = toIsoDate(new Date());
  const dayWatchTimer = window.setInterval(() => {
    const today = toIsoDate(new Date());
    if (today === lastDay) return;
    lastDay = today;
    if (filters.presetId !== 'custom') {
      applyPreset(filters.presetId);
    }
  }, 60 * 1000);
  onUnmounted(() => window.clearInterval(dayWatchTimer));

  // Debounce trip-инпута: тяжёлый LIKE-поиск не стартует на каждый символ.
  const tripQuery = ref(filters.trip);
  const debouncedSetTrip = useDebounceFn((value) => {
    tripQuery.value = value;
  }, 400);
  watch(
    () => filters.trip,
    (value) => debouncedSetTrip(value)
  );

  const filtersKey = computed(() => {
    const { trip, ...rest } = filters;
    void trip;
    return JSON.stringify({ ...rest, trip: tripQuery.value });
  });

  const fullHistoryKey = computed(() => {
    const { dateFrom, dateTo, trip, ...rest } = filters;
    void dateFrom;
    void dateTo;
    void trip;
    return JSON.stringify({ ...rest, trip: tripQuery.value });
  });

  return { filters, resetFilters, applySnapshot, tripQuery, filtersKey, fullHistoryKey };
}

export function useStatisticsPage(activeTab = null) {
  const queryClient = useQueryClient();
  const { filters, resetFilters, applySnapshot, filtersKey, fullHistoryKey } =
    useStatisticsFilters();
  const tripsSorting = ref([{ id: 'stat_date', desc: true }]);
  const tripsPage = ref(1);

  // Запрос активен, если вкладка не передана или совпадает: не дёргаем
  // все 13 эндпоинтов на каждое изменение фильтров — только открытую вкладку.
  const tabEnabled = (tab) =>
    computed(() => !activeTab || !activeTab.value || activeTab.value === tab);

  const optionsQuery = useQuery({
    queryKey: ['statistics', 'filter-options'],
    queryFn: fetchFilterOptions,
    staleTime: 5 * 60 * 1000,
  });

  const kpisQuery = useQuery({
    queryKey: computed(() => ['statistics', 'kpis', filtersKey.value]),
    queryFn: () => fetchKpis(filters),
    staleTime: 30 * 1000,
  });

  const tripsQuery = useQuery({
    queryKey: computed(() => [
      'statistics',
      'trips',
      filtersKey.value,
      tripsSorting.value,
      tripsPage.value,
    ]),
    queryFn: () => {
      const sort = tripsSorting.value[0] || { id: 'stat_date', desc: true };
      return fetchTrips({
        filters,
        sortBy: sort.id,
        asc: !sort.desc,
        limit: TRIPS_PAGE_SIZE,
        offset: (tripsPage.value - 1) * TRIPS_PAGE_SIZE,
      });
    },
    enabled: tabEnabled('trips'),
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const tripsByDayQuery = useQuery({
    queryKey: computed(() => ['statistics', 'trips-by-day', filtersKey.value]),
    queryFn: () => fetchTripsByDay(filters),
    enabled: tabEnabled('trips'),
    staleTime: 60 * 1000,
  });

  const logistsQuery = useQuery({
    queryKey: computed(() => ['statistics', 'by-logist', filtersKey.value]),
    queryFn: () => fetchByLogist(filters),
    enabled: tabEnabled('logists'),
    staleTime: 60 * 1000,
  });

  const directionsQuery = useQuery({
    queryKey: computed(() => ['statistics', 'by-direction', filtersKey.value]),
    queryFn: () => fetchByDirection(filters, 5),
    enabled: tabEnabled('directions'),
    staleTime: 60 * 1000,
  });

  const directionsAllHistoryQuery = useQuery({
    queryKey: computed(() => ['statistics', 'by-direction-all', fullHistoryKey.value]),
    queryFn: () => fetchByDirection({ ...filters, dateFrom: '', dateTo: '' }, 1),
    enabled: tabEnabled('directions'),
    staleTime: 60 * 1000,
  });

  const directionLifecycleQuery = useQuery({
    queryKey: computed(() => ['statistics', 'direction-lifecycle', filtersKey.value]),
    queryFn: () => fetchDirectionLifecycle(filters, 1),
    enabled: tabEnabled('directions'),
    staleTime: 60 * 1000,
  });

  const segmentsQuery = useQuery({
    queryKey: computed(() => ['statistics', 'segments', filtersKey.value]),
    queryFn: () => fetchSegments(filters),
    enabled: tabEnabled('segments'),
    staleTime: 60 * 1000,
  });

  const statusSummaryQuery = useQuery({
    queryKey: computed(() => ['statistics', 'status-summary', filtersKey.value]),
    queryFn: () => fetchStatusSummary(filters),
    enabled: tabEnabled('statuses'),
    staleTime: 60 * 1000,
  });

  const statusByLogistQuery = useQuery({
    queryKey: computed(() => ['statistics', 'status-by-logist', filtersKey.value]),
    queryFn: () => fetchStatusByLogist(filters),
    enabled: tabEnabled('statuses'),
    staleTime: 60 * 1000,
  });

  const botSummaryQuery = useQuery({
    queryKey: computed(() => ['statistics', 'bot-summary', filtersKey.value]),
    queryFn: () => fetchBotSummary(filters),
    enabled: tabEnabled('bot'),
    staleTime: 60 * 1000,
  });

  const botByDayQuery = useQuery({
    queryKey: computed(() => ['statistics', 'bot-by-day', filtersKey.value]),
    queryFn: () => fetchBotByDay(filters),
    enabled: tabEnabled('bot'),
    staleTime: 60 * 1000,
  });

  const botAuctionsQuery = useQuery({
    queryKey: computed(() => ['statistics', 'bot-auctions', filtersKey.value]),
    queryFn: () => fetchBotAuctions(filters),
    enabled: tabEnabled('bot'),
    staleTime: 60 * 1000,
  });

  // Шаг корзин вкладки «Динамика» (persist), сброс — на день
  const loadStep = () => {
    try {
      const raw = window.localStorage.getItem(STEP_STORAGE_KEY);
      return VALID_STEPS.includes(raw) ? raw : 'day';
    } catch (e) {
      return 'day';
    }
  };
  const step = ref(loadStep());
  watch(step, (value) => {
    try {
      window.localStorage.setItem(STEP_STORAGE_KEY, value);
    } catch (e) {
      /* localStorage может быть недоступен — молча игнорируем */
    }
  });

  // Час × период длиннее ~41 дня превысил бы кэп корзин (1000):
  // запрос не отправляем — вкладка показывает подсказку о сужении периода.
  const hourCapExceeded = computed(() => {
    if (step.value !== 'hour' || !filters.dateFrom || !filters.dateTo) return false;
    const from = new Date(filters.dateFrom).getTime();
    const to = new Date(filters.dateTo).getTime();
    if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return false;
    return Math.ceil((to - from) / DAY_MS) + 1 > HOUR_STEP_MAX_DAYS;
  });

  const temporalQuery = useQuery({
    queryKey: computed(() => ['statistics', 'temporal', filtersKey.value, step.value]),
    queryFn: () => fetchTemporal(filters, step.value),
    enabled: computed(() => tabEnabled('dynamics').value && !hourCapExceeded.value),
    staleTime: 60 * 1000,
  });

  const refreshAll = async () => {
    await queryClient.invalidateQueries({ queryKey: ['statistics'] });
  };

  // Сброс фильтров вместе со шагом корзин: период — текущий месяц, шаг — день
  const resetFiltersWithStep = () => {
    resetFilters();
    step.value = 'day';
  };

  return {
    filters,
    filtersKey,
    resetFilters: resetFiltersWithStep,
    applyFiltersSnapshot: applySnapshot,
    step,
    hourCapExceeded,
    optionsQuery,
    kpisQuery,
    tripsQuery,
    tripsByDayQuery,
    tripsSorting,
    tripsPage,
    logistsQuery,
    directionsQuery,
    directionsAllHistoryQuery,
    directionLifecycleQuery,
    segmentsQuery,
    statusSummaryQuery,
    statusByLogistQuery,
    botSummaryQuery,
    botByDayQuery,
    botAuctionsQuery,
    temporalQuery,
    refreshAll,
  };
}
